// src/data/states/index.js
//
// STATES_INDEX — lightweight summary of all 50 states.
//
// Each entry contains just the fields needed by the /states index page,
// the StatePage "neighboring states" cross-link cards, the StatePage
// author-state-count credibility surface, and the CityPage state-name
// lookups. The full record (top cities, contaminants, climate, cleaning
// calendar, housing stock, linked articles, water profile notes, etc.)
// lives in src/data/states/<slug>.js and is loaded by slug.
//
// This module is at ~12-15 KB raw / ~5 KB gzip — roughly 1/25th of the
// monolithic states.js it replaces for these use cases.
//
// Generated by scripts/split-states.mjs (session 35).

export const STATES_INDEX = [
  {
    "slug": "arizona",
    "name": "Arizona",
    "abbreviation": "AZ",
    "region": "West",
    "assignedAuthor": "drew-giordano",
    "summary": "Hard to extremely hard at the tap across nearly the entire populated state; the Colorado River blend is the through-line.",
    "hardnessLow": 145,
    "hardnessHigh": 420,
    "dominantTier": "very hard (Chandler); extremely hard (Mesa, Scottsdale, Phoenix); 2 of 6 covered cities publish no figure",
    "topCitiesCount": 6,
    "contaminantsCount": 5
  },
  {
    "slug": "florida",
    "name": "Florida",
    "abbreviation": "FL",
    "region": "South",
    "assignedAuthor": "joann-giordano",
    "summary": "Hard to very hard at the tap in the covered cities that publish: Orlando and St. Petersburg sit in the hard band, Tampa and Jacksonville in the very hard. What sets Florida apart is the salt aerosol.",
    "hardnessLow": 110,
    "hardnessHigh": 290,
    "dominantTier": "hard (St. Petersburg, Orlando); very hard (Jacksonville, Tampa); 2 of 6 covered cities publish no figure",
    "topCitiesCount": 6,
    "contaminantsCount": 6
  },
  {
    "slug": "illinois",
    "name": "Illinois",
    "abbreviation": "IL",
    "region": "Midwest",
    "assignedAuthor": "mara-whitfield",
    "summary": "A state divided by source. Chicago's Lake Michigan water is hard by its published figures, Aurora runs moderately hard to hard, and Springfield's Lake Springfield supply sits in the moderately hard band. Private groundwater outside the utilities varies from well to well.",
    "hardnessLow": 140,
    "hardnessHigh": 345,
    "dominantTier": "moderately hard (Springfield); hard (Aurora, Chicago); 3 of 6 covered cities publish no figure",
    "topCitiesCount": 6,
    "contaminantsCount": 6
  },
  {
    "slug": "texas",
    "name": "Texas",
    "abbreviation": "TX",
    "region": "South",
    "assignedAuthor": "jerry-davenport",
    "summary": "Texas water depends on the source, and the covered utilities that publish a hardness figure span every band from soft to extremely hard, so no statewide range describes a given tap. Each city page carries its own utility's figure. San Antonio's Edwards Aquifer supply is the extremely hard end, and El Paso's range reaches very hard.",
    "hardnessLow": 180,
    "hardnessHigh": 600,
    "dominantTier": "moderately hard (Austin); hard (El Paso, Houston, Fort Worth); extremely hard (San Antonio); 1 of 6 covered cities publishes no figure",
    "topCitiesCount": 6,
    "contaminantsCount": 6
  },
  {
    "slug": "new-york",
    "name": "New York",
    "abbreviation": "NY",
    "region": "Northeast",
    "assignedAuthor": "abby-giordano",
    "summary": "A state of several water profiles. Catskill and Delaware surface water serves New York City and Yonkers, soft to moderately hard; Long Island's aquifer supply at Hempstead is soft; and the upstate lake-fed cities run moderately hard in Buffalo and Rochester and hard in Syracuse.",
    "hardnessLow": 35,
    "hardnessHigh": 350,
    "dominantTier": "soft (Hempstead); moderately hard (Rochester, Buffalo, New York City, Yonkers); hard (Syracuse)",
    "topCitiesCount": 6,
    "contaminantsCount": 6
  },
  {
    "slug": "california",
    "name": "California",
    "abbreviation": "CA",
    "region": "West",
    "assignedAuthor": "easton-giordano",
    "summary": "The most varied water profile of any state — a patchwork of six major districts, each with its own source, treatment, and hardness profile, plus a meaningful population of Central Valley wells.",
    "hardnessLow": 60,
    "hardnessHigh": 450,
    "dominantTier": "moderately hard (Oakland, San Francisco); hard (Fresno); very hard (San Diego, Los Angeles); extremely hard (San Jose)",
    "topCitiesCount": 6,
    "contaminantsCount": 6
  },
  {
    "slug": "new-jersey",
    "name": "New Jersey",
    "abbreviation": "NJ",
    "region": "Northeast",
    "assignedAuthor": "derek-giordano",
    "summary": "Moderately hard across most of the state on a patchwork of surface and groundwater supplies; the pre-war housing stock and the spring pollen calendar are bigger working considerations than the water.",
    "hardnessLow": 80,
    "hardnessHigh": 240,
    "dominantTier": "soft (Newark); moderately hard (Paterson, Trenton, Jersey City); hard (Princeton, Cherry Hill)",
    "topCitiesCount": 6,
    "contaminantsCount": 6
  },
  {
    "slug": "michigan",
    "name": "Michigan",
    "abbreviation": "MI",
    "region": "Midwest",
    "assignedAuthor": "jan-davenport",
    "summary": "Detroit-metro surface water runs moderately hard, as do Lansing and Warren. Grand Rapids on Lake Michigan and Ann Arbor publish in the hard band. Outstate towns lean more on wells, and the whole state carries a heavy salt-aerosol overlay in winter.",
    "hardnessLow": 90,
    "hardnessHigh": 420,
    "dominantTier": "moderately hard (Detroit, Warren, Lansing); hard (Grand Rapids, Ann Arbor); 1 of 6 covered cities publishes no figure",
    "topCitiesCount": 6,
    "contaminantsCount": 6
  },
  {
    "slug": "ohio",
    "name": "Ohio",
    "abbreviation": "OH",
    "region": "Midwest",
    "assignedAuthor": "jan-davenport",
    "summary": "Three distinct urban water profiles: Cleveland on Lake Erie, Columbus on Scioto-and-groundwater, Cincinnati on the Ohio River. Cleveland, Columbus and Akron publish moderately hard water, Cincinnati and Dayton hard. Outstate towns on the limestone belt lean on wells, and winter salt severity runs a north-south gradient.",
    "hardnessLow": 95,
    "hardnessHigh": 425,
    "dominantTier": "moderately hard (Columbus, Cleveland, Akron); hard (Dayton, Cincinnati); 1 of 6 covered cities publishes no figure",
    "topCitiesCount": 6,
    "contaminantsCount": 6
  },
  {
    "slug": "pennsylvania",
    "name": "Pennsylvania",
    "abbreviation": "PA",
    "region": "Northeast",
    "assignedAuthor": "tony-petruzzi",
    "summary": "Philadelphia's surface water runs moderately hard to hard. The limestone valleys run harder: Lancaster reaches very hard and Allentown extremely hard. Pre-1900 housing density dictates the working calendar across most of the state.",
    "hardnessLow": 110,
    "hardnessHigh": 380,
    "dominantTier": "moderately hard (Philadelphia); hard (Lancaster); extremely hard (Allentown); 3 of 6 covered cities publish no figure",
    "topCitiesCount": 6,
    "contaminantsCount": 6
  },
  {
    "slug": "massachusetts",
    "name": "Massachusetts",
    "abbreviation": "MA",
    "region": "Northeast",
    "assignedAuthor": "abby-giordano",
    "summary": "Soft surface water across the Boston metro and most of the eastern half of the state from the MWRA Quabbin-Wachusett system, and Worcester and Springfield report soft water on their own reservoirs. Pre-1900 housing density and a four-month salt season are the working drivers.",
    "hardnessLow": 18,
    "hardnessHigh": 220,
    "dominantTier": "soft (Worcester, Springfield, Boston); 3 of 6 covered cities publish no figure",
    "topCitiesCount": 6,
    "contaminantsCount": 6
  },
  {
    "slug": "washington",
    "name": "Washington",
    "abbreviation": "WA",
    "region": "West",
    "assignedAuthor": "easton-giordano",
    "summary": "Soft surface water across the Puget Sound corridor from the Cascade snowmelt watersheds; Seattle, Bellevue and Tacoma all publish soft. East of the Cascades it splits: Spokane's aquifer water reaches extremely hard while Yakima stays soft. West of the mountains the cleaning driver is the moss-and-algae substrate, not the water.",
    "hardnessLow": 15,
    "hardnessHigh": 280,
    "dominantTier": "soft (Bellevue, Seattle, Yakima, Tacoma); moderately hard (Vancouver); very hard (Spokane)",
    "topCitiesCount": 6,
    "contaminantsCount": 6
  },
  {
    "slug": "georgia",
    "name": "Georgia",
    "abbreviation": "GA",
    "region": "Southeast",
    "assignedAuthor": "elly-giordano",
    "summary": "Metro Atlanta runs on Chattahoochee surface water; Marietta, the one covered utility that publishes a figure, is soft. The outer suburbs lean more on groundwater (Buford sits on limestone). Coastal Georgia (Savannah, Brunswick) draws on the Floridan aquifer, with chloride influence.",
    "hardnessLow": 55,
    "hardnessHigh": 280,
    "dominantTier": "soft (Marietta); 5 of 6 covered cities publish no figure",
    "topCitiesCount": 6,
    "contaminantsCount": 6
  },
  {
    "slug": "oregon",
    "name": "Oregon",
    "abbreviation": "OR",
    "region": "Pacific Northwest",
    "assignedAuthor": "easton-giordano",
    "summary": "Soft on the west side of the Cascades, where the Bull Run, McKenzie and Clackamas surface sources feed Portland, Eugene and the metro. East of the mountains the sources shift to Deschutes and Klamath basin groundwater, yet Bend's volcanic-aquifer supply publishes soft as well.",
    "hardnessLow": 8,
    "hardnessHigh": 280,
    "dominantTier": "soft (Portland, Bend, Eugene); 2 of 5 covered cities publish no figure",
    "topCitiesCount": 6,
    "contaminantsCount": 6
  },
  {
    "slug": "colorado",
    "name": "Colorado",
    "abbreviation": "CO",
    "region": "Mountain West",
    "assignedAuthor": "easton-giordano",
    "summary": "Denver Water's tap runs soft to moderately hard as it blends Dillon and Williams Fork high-altitude reservoir water with South Platte intake. Boulder publishes a soft figure and Fort Collins calls its own water naturally soft, while Aurora reaches the hard band. Colorado Springs and the Western Slope (Grand Junction) run their own sources. City by city, the Front Range is a patchwork.",
    "hardnessLow": 30,
    "hardnessHigh": 280,
    "dominantTier": "soft (Boulder); moderately hard (Denver); hard (Aurora); naturally soft by the utility's own description (Fort Collins); 1 of 5 covered cities publishes no figure",
    "topCitiesCount": 7,
    "contaminantsCount": 6
  },
  {
    "slug": "north-carolina",
    "name": "North Carolina",
    "abbreviation": "NC",
    "region": "Southeast",
    "assignedAuthor": "elly-giordano",
    "summary": "Soft at the tap in all seven covered cities, whether Charlotte on the Catawba or the Triangle on Falls Lake, Jordan Lake, Lake Michie and the OWASA reservoirs. Asheville in the western mountains has some of the softest water we track. The eastern coastal plain blends in groundwater, and Wilmington's tap is still soft.",
    "hardnessLow": 30,
    "hardnessHigh": 180,
    "dominantTier": "soft (Wilmington, Greensboro, Charlotte, Cary, Raleigh, Durham, Asheville)",
    "topCitiesCount": 7,
    "contaminantsCount": 6
  },
  {
    "slug": "minnesota",
    "name": "Minnesota",
    "abbreviation": "MN",
    "region": "Upper Midwest",
    "assignedAuthor": "linnea-jorgensen",
    "summary": "A meaningful split between Twin Cities surface-water sources (Mississippi River for Minneapolis, glacial-lake reservoirs for Saint Paul) and the outer-ring suburban groundwater pulled from the Prairie du Chien-Jordan aquifer. The hardness difference between adjacent municipalities can be substantial. Rochester's groundwater is the extreme case, extremely hard by its utility's figure, while Duluth on Lake Superior is soft.",
    "hardnessLow": 60,
    "hardnessHigh": 220,
    "dominantTier": "soft (Duluth); moderately hard (Minneapolis, Saint Paul, Bloomington); extremely hard (Rochester)",
    "topCitiesCount": 7,
    "contaminantsCount": 6
  },
  {
    "slug": "wisconsin",
    "name": "Wisconsin",
    "abbreviation": "WI",
    "region": "Upper Midwest",
    "assignedAuthor": "mara-whitfield",
    "summary": "Two distinct water profiles divide the state. The eastern lakeshore corridor (Milwaukee, Racine, Kenosha, Sheboygan, Green Bay) pulls Lake Michigan and publishes hard. Interior and western Wisconsin runs deep sandstone-and-dolomite aquifer water that is harder still and iron-bearing, with Madison extremely hard.",
    "hardnessLow": 80,
    "hardnessHigh": 400,
    "dominantTier": "hard (Kenosha, Racine, Milwaukee, Green Bay); extremely hard (Madison)",
    "topCitiesCount": 7,
    "contaminantsCount": 6
  },
  {
    "slug": "virginia",
    "name": "Virginia",
    "abbreviation": "VA",
    "region": "Mid-Atlantic",
    "assignedAuthor": "tony-petruzzi",
    "summary": "Three distinct profiles divide the state. The DC-metro corridor draws from the Potomac; Arlington reports hard water and Alexandria moderately hard to very hard. The Richmond metro and central Piedmont draw from the James River, moderately hard in Richmond. The Tidewater is soft to moderately hard at the tap but dominated by salt-aerosol load. West of the Blue Ridge, supplies draw on carbonate-bedrock groundwater.",
    "hardnessLow": 70,
    "hardnessHigh": 280,
    "dominantTier": "soft (Virginia Beach, Chesapeake, Norfolk); moderately hard (Richmond, Hampton, Newport News); hard (Arlington, Alexandria)",
    "topCitiesCount": 8,
    "contaminantsCount": 6
  },
  {
    "slug": "tennessee",
    "name": "Tennessee",
    "abbreviation": "TN",
    "region": "Mid-South",
    "assignedAuthor": "cal-hatcher",
    "summary": "Three distinct profiles divide the state. Memphis pulls from the Memphis Sand aquifer and runs soft. Metro Nashville pulls the Cumberland River, moderately hard, while Williamson County and the Middle Tennessee suburbs pull karst-aquifer water. Knoxville on the Tennessee River is moderately hard and Chattanooga soft to moderately hard; smaller mountain-top East TN systems run their own sources.",
    "hardnessLow": 80,
    "hardnessHigh": 280,
    "dominantTier": "soft (Memphis); moderately hard (Clarksville, Knoxville, Nashville, Chattanooga)",
    "topCitiesCount": 8,
    "contaminantsCount": 6
  },
  {
    "slug": "indiana",
    "name": "Indiana",
    "abbreviation": "IN",
    "region": "Midwest",
    "assignedAuthor": "mara-whitfield",
    "summary": "Three distinct profiles divide the state. Northwest Indiana runs Lake Michigan supply continuous with Chicago, and Gary reports it hard. Central Indiana is where the figures climb: Carmel's published range reaches very hard, and Indianapolis and Fishers publish extremely hard water. Southern Indiana is karst country (Bedford, French Lick), though Bloomington's tap is soft. Evansville on the Ohio River and Fort Wayne both report hard water.",
    "hardnessLow": 110,
    "hardnessHigh": 220,
    "dominantTier": "soft (Bloomington); hard (Gary, Evansville, Fort Wayne); very hard (Carmel); extremely hard (Fishers, Indianapolis); 1 of 8 covered cities publishes no figure",
    "topCitiesCount": 8,
    "contaminantsCount": 6
  },
  {
    "slug": "maryland",
    "name": "Maryland",
    "abbreviation": "MD",
    "region": "Mid-Atlantic",
    "assignedAuthor": "tony-petruzzi",
    "summary": "Maryland runs four distinct water profiles tightly clustered geographically. The DC-Maryland WSSC suburbs run Potomac-source continuous with Northern Virginia. Baltimore City and County run softer Patapsco-source. Eastern Shore runs shallow Coastal Plain aquifer with brackish-bay salt-aerosol on waterfront stock. Western Maryland flips to Ridge-and-Valley karst groundwater.",
    "hardnessLow": 80,
    "hardnessHigh": 260,
    "dominantTier": "moderately hard (Baltimore); hard (Germantown, Silver Spring); 2 of 5 covered cities publish no figure",
    "topCitiesCount": 10,
    "contaminantsCount": 7
  },
  {
    "slug": "delaware",
    "name": "Delaware",
    "abbreviation": "DE",
    "region": "Mid-Atlantic",
    "assignedAuthor": "tony-petruzzi",
    "summary": "Delaware splits cleanly by county. New Castle County's two covered cities run municipal systems, Wilmington on Brandywine Creek surface water that spans moderately hard to very hard across its published range. Kent County flips to City of Dover and Tidewater Utilities groundwater. Sussex coastal beach communities run Tidewater Utilities and smaller systems, with brackish-bay plus open-Atlantic salt aerosol defining coastal chemistry. Brandywine Valley well-water carries a Piedmont iron-clay fraction.",
    "hardnessLow": 100,
    "hardnessHigh": 220,
    "dominantTier": "moderately hard (Dover, Newark); hard (Wilmington); 2 of 5 covered cities publish no figure",
    "topCitiesCount": 8,
    "contaminantsCount": 6
  },
  {
    "slug": "kentucky",
    "name": "Kentucky",
    "abbreviation": "KY",
    "region": "Ohio Valley",
    "assignedAuthor": "wade-marler",
    "summary": "Kentucky splits into five chemistry zones. Louisville runs Louisville Water Company Ohio River-source. Lexington runs Kentucky-American Kentucky River. Bluegrass horse-country wells (Woodford, Bourbon, Scott, Jessamine) run karst. Cave country wells (Bowling Green, Mammoth Cave region) run karst. Northern Kentucky runs Ohio River continuous with Cincinnati. Eastern Kentucky carries its own coalfield-heritage exposure profile.",
    "hardnessLow": 100,
    "hardnessHigh": 300,
    "dominantTier": "hard (Lexington, Bowling Green, Louisville, Covington); 1 of 5 covered cities publishes no figure",
    "topCitiesCount": 8,
    "contaminantsCount": 6
  },
  {
    "slug": "connecticut",
    "name": "Connecticut",
    "abbreviation": "CT",
    "region": "New England",
    "assignedAuthor": "abby-giordano",
    "summary": "Connecticut splits into three chemistry zones along Gold Coast / Central Valley / Eastern boundaries. Fairfield County and the Gold Coast run Aquarion Water Company surface-source water, soft at the tap in Bridgeport and Stamford. The central Connecticut River valley runs MDC. Eastern Connecticut runs a patchwork of smaller municipal systems plus substantial private well supply. The shoreline carries Long Island Sound salt-aerosol exposure.",
    "hardnessLow": 60,
    "hardnessHigh": 220,
    "dominantTier": "soft (Bridgeport, Stamford); 3 of 5 covered cities publish no figure",
    "topCitiesCount": 8,
    "contaminantsCount": 6
  },
  {
    "slug": "south-carolina",
    "name": "South Carolina",
    "abbreviation": "SC",
    "region": "Southeast",
    "assignedAuthor": "elly-giordano",
    "summary": "South Carolina splits into four chemistry zones along Upstate / Midlands / Lowcountry / Grand Strand boundaries. The Upstate runs Greenville Water's Table Rock and North Saluda reservoirs. The Midlands runs Columbia Water's Lake Murray supply. The Lowcountry runs Charleston Water System's Edisto-and-Bushy-Park supply with substantial coastal salt-aerosol exposure. The Grand Strand runs Grand Strand Water and Sewer Authority with open-Atlantic salt aerosol. Every covered utility that publishes, from Rock Hill to Charleston, reports soft water.",
    "hardnessLow": 30,
    "hardnessHigh": 130,
    "dominantTier": "soft (Charleston, North Charleston, Columbia, Rock Hill); 1 of 5 covered cities publishes no figure",
    "topCitiesCount": 8,
    "contaminantsCount": 6
  },
  {
    "slug": "iowa",
    "name": "Iowa",
    "abbreviation": "IA",
    "region": "Midwest",
    "assignedAuthor": "jan-davenport",
    "summary": "Iowa holds the hardest published municipal water we track: Sioux City tops the national ranking, and Davenport's Mississippi River supply reaches extremely hard. Iowa City is the soft exception. Des Moines Water Works runs Des Moines and Raccoon River supply, and Cedar Rapids runs the Cedar River. Rural private well supply runs on limestone-aquifer-influenced groundwater.",
    "hardnessLow": 200,
    "hardnessHigh": 500,
    "dominantTier": "soft (Iowa City); hard (Cedar Rapids); very hard (Davenport); extremely hard (Sioux City); 1 of 5 covered cities publishes no figure",
    "topCitiesCount": 8,
    "contaminantsCount": 6
  },
  {
    "slug": "missouri",
    "name": "Missouri",
    "abbreviation": "MO",
    "region": "Midwest",
    "assignedAuthor": "jan-davenport",
    "summary": "Missouri splits into four chemistry zones along Kansas City / St. Louis / Springfield-Ozarks / Bootheel boundaries. Kansas City Water draws Missouri River-source water and publishes it moderately hard. St. Louis runs Mississippi-and-Missouri River-source supply that is softened at the plant yet still lands in the hard band. Springfield runs City Utilities Fellows Lake plus Ozark-karst well-water in surrounding counties. The Bootheel runs mixed alluvial supply.",
    "hardnessLow": 60,
    "hardnessHigh": 350,
    "dominantTier": "moderately hard (Kansas City); hard (Springfield, St. Louis, Independence); very hard (Columbia)",
    "topCitiesCount": 8,
    "contaminantsCount": 6
  },
  {
    "slug": "rhode-island",
    "name": "Rhode Island",
    "abbreviation": "RI",
    "region": "New England",
    "assignedAuthor": "abby-giordano",
    "summary": "Most of Rhode Island runs on Providence Water's Scituate Reservoir or supply interconnected with it, and Providence, Cranston and East Providence all describe their water as soft. The working problem is not the water. It is the maritime exposure on coastal stock and the substantial pre-1800 colonial heritage concentration in Newport, Providence, and the broader Narragansett Bay corridor.",
    "hardnessLow": 30,
    "hardnessHigh": 80,
    "dominantTier": "soft by the utility's own description (Cranston, East Providence, Providence); 2 of 5 covered cities publish no figure",
    "topCitiesCount": 8,
    "contaminantsCount": 6
  },
  {
    "slug": "kansas",
    "name": "Kansas",
    "abbreviation": "KS",
    "region": "Midwest",
    "assignedAuthor": "jan-davenport",
    "summary": "Four of the five covered Kansas utilities publish in the hard band, and Kansas City's Missouri River supply is extremely hard. Wichita Water Department draws from Cheney Reservoir and the Equus Beds aquifer. Topeka Water draws the Kansas River and Lake Sherwood. Lawrence runs the Kansas River. Western Kansas wheat-belt municipals and rural well supply run on Ogallala and Dakota aquifer groundwater.",
    "hardnessLow": 200,
    "hardnessHigh": 500,
    "dominantTier": "hard (Olathe, Topeka, Wichita, Overland Park); extremely hard (Kansas City)",
    "topCitiesCount": 8,
    "contaminantsCount": 6
  },
  {
    "slug": "nevada",
    "name": "Nevada",
    "abbreviation": "NV",
    "region": "Southwest",
    "assignedAuthor": "drew-giordano",
    "summary": "Nevada splits cleanly into two zones along the Las Vegas / Reno-Tahoe boundaries. The Las Vegas Valley draws Lake Mead and the Colorado River, and Las Vegas, Henderson and North Las Vegas all publish extremely hard water after the long Lake Mead retention. Reno runs Truckee Meadows Water Authority's Truckee River source, far softer on its Sierra Nevada snowmelt profile. Carson City runs mixed supply. Rural Nevada wells vary widely.",
    "hardnessLow": 80,
    "hardnessHigh": 450,
    "dominantTier": "moderately hard (Sparks); hard (Reno); extremely hard (Henderson, Las Vegas, North Las Vegas)",
    "topCitiesCount": 8,
    "contaminantsCount": 6
  },
  {
    "slug": "alabama",
    "name": "Alabama",
    "abbreviation": "AL",
    "region": "Southeast",
    "assignedAuthor": "elly-giordano",
    "summary": "Alabama runs four chemistry zones. Birmingham draws on the Cahaba River and the Lake Purdy reservoir, and Huntsville on the Tennessee River. The Black Belt agricultural band runs karst-aquifer groundwater through Selma, Demopolis, Marengo and Wilcox County corridors. Mobile and the Gulf Coast run on Mobile Area Water and Sewer's Big Creek Lake: soft at the tap, with a heavy Gulf and Mobile Bay salt-aerosol overlay on top.",
    "hardnessLow": 50,
    "hardnessHigh": 280,
    "dominantTier": "soft (Mobile, Montgomery); moderately hard (Huntsville, Birmingham); 1 of 5 covered cities publishes no figure",
    "topCitiesCount": 8,
    "contaminantsCount": 6
  },
  {
    "slug": "oklahoma",
    "name": "Oklahoma",
    "abbreviation": "OK",
    "region": "South",
    "assignedAuthor": "jan-davenport",
    "summary": "Oklahoma runs three chemistry zones. Oklahoma City Water Utilities runs a composite supply (Lake Hefner, Lake Overholser, Lake Stanley Draper, plus Atoka and McGee Creek pipeline imports). Tulsa Water draws Spavinaw Creek and Lake Eucha in the Ozark watershed and reports moderately hard to hard water. The Panhandle and western Oklahoma run on Ogallala and Permian aquifer well-water.",
    "hardnessLow": 80,
    "hardnessHigh": 500,
    "dominantTier": "moderately hard (Tulsa); 4 of 5 covered cities publish no figure",
    "topCitiesCount": 8,
    "contaminantsCount": 6
  },
  {
    "slug": "utah",
    "name": "Utah",
    "abbreviation": "UT",
    "region": "Mountain West",
    "assignedAuthor": "easton-giordano",
    "summary": "Utah runs as a hardness gradient along the Wasatch Front. Salt Lake City Department of Public Utilities draws Wasatch-canyon surface supply, with seasonal snowmelt variation. Jordan Valley Water Conservancy District serves suburban Salt Lake County. The Utah Valley municipal corridor (Provo, Orem, American Fork, Lehi) runs its own municipal supplies, with private wells on the rural-exurban edge. Park City and Deer Valley municipal supply sits beside Heber Valley well-water exposure.",
    "hardnessLow": 130,
    "hardnessHigh": 400,
    "dominantTier": "hard (West Valley City); very hard (Provo, West Jordan); extremely hard (Salt Lake City, Orem)",
    "topCitiesCount": 9,
    "contaminantsCount": 6
  },
  {
    "slug": "mississippi",
    "name": "Mississippi",
    "abbreviation": "MS",
    "region": "Southeast",
    "assignedAuthor": "elly-giordano",
    "summary": "Mississippi runs as four working zones along a north-south axis. The DeSoto County corridor (Southaven, Olive Branch, Hernando) draws on the Memphis Sand aquifer, continuous with the Memphis profile across the state line. Jackson metro and central Mississippi run Pearl River-source surface and well-supplemented systems. The Delta agricultural belt (Greenwood, Cleveland, Greenville, Clarksdale) is well-water on most rural systems. On the Gulf Coast corridor (Biloxi, Gulfport, Pascagoula, Ocean Springs, Bay St. Louis) the open-Gulf salt-aerosol overlay matters more than the tap.",
    "hardnessLow": 80,
    "hardnessHigh": 340,
    "dominantTier": "not published",
    "topCitiesCount": 8,
    "contaminantsCount": 6
  },
  {
    "slug": "louisiana",
    "name": "Louisiana",
    "abbreviation": "LA",
    "region": "South",
    "assignedAuthor": "elly-giordano",
    "summary": "Louisiana runs as three distinct working zones. New Orleans and the metro corridor on Sewerage and Water Board Mississippi River-source supply with substantial organic-load fraction. Baton Rouge and the central Louisiana corridor on Baton Rouge Water Company Southern Hills aquifer and Mississippi River-supplemented systems. North Louisiana through Shreveport, Monroe, and the Sportsman's Paradise corridor on local aquifer and reservoir systems. The Acadiana corridor (Lafayette, Lake Charles, New Iberia) runs on Chicot aquifer and reservoir-source systems.",
    "hardnessLow": 90,
    "hardnessHigh": 280,
    "dominantTier": "soft (Shreveport); moderately hard (Lake Charles); 3 of 5 covered cities publish no figure",
    "topCitiesCount": 9,
    "contaminantsCount": 7
  },
  {
    "slug": "nebraska",
    "name": "Nebraska",
    "abbreviation": "NE",
    "region": "Midwest",
    "assignedAuthor": "jan-davenport",
    "summary": "Nebraska runs as a hardness gradient west across the state. Omaha and the Missouri River corridor on Metropolitan Utilities District Missouri River and aquifer-supplemented supply. Lincoln and the Lower Platte corridor on Lincoln Water System aquifer and Platte River-supplemented supply. Central Nebraska through Grand Island, Kearney, and Hastings on local aquifer systems, and Grand Island reports extremely hard water. Western Nebraska and the Panhandle through North Platte, Scottsbluff, and Sidney flips to Ogallala-aquifer rural well-water, with a sub-micron suspended-particulate fraction on the worst-affected systems.",
    "hardnessLow": 140,
    "hardnessHigh": 450,
    "dominantTier": "hard (Bellevue, Omaha); very hard (Lincoln); extremely hard (Grand Island); 1 of 5 covered cities publishes no figure",
    "topCitiesCount": 9,
    "contaminantsCount": 6
  },
  {
    "slug": "new-mexico",
    "name": "New Mexico",
    "abbreviation": "NM",
    "region": "Southwest",
    "assignedAuthor": "easton-giordano",
    "summary": "New Mexico runs as zones set by aquifer source. Albuquerque and the Middle Rio Grande corridor on Albuquerque Bernalillo County Water Utility Authority aquifer and San Juan-Chama Drinking Water supply, the one covered utility that publishes and a hard one. Santa Fe and the northern corridor on Santa Fe Buckman Direct Diversion and aquifer-supplemented supply. Las Cruces and the southern Rio Grande corridor on Las Cruces Utilities aquifer supply. Roswell, Carlsbad, and the southeastern Permian Basin corridor on Pecos River alluvial-aquifer and well-water supply. Farmington, Gallup, and the western reservation-adjacent corridor on San Juan River and aquifer supply.",
    "hardnessLow": 140,
    "hardnessHigh": 450,
    "dominantTier": "hard (Albuquerque); 4 of 5 covered cities publish no figure",
    "topCitiesCount": 9,
    "contaminantsCount": 7
  },
  {
    "slug": "north-dakota",
    "name": "North Dakota",
    "abbreviation": "ND",
    "region": "Midwest",
    "assignedAuthor": "jan-davenport",
    "summary": "North Dakota runs as three working zones. The Red River Valley corridor (Fargo, Grand Forks, West Fargo) on Fargo Water Treatment Plant Red River and Sheyenne River-source supply with substantial seasonal organic-load variation. The central Missouri River corridor (Bismarck, Mandan, Williston) on Missouri River-source municipal supply. The western North Dakota Bakken-corridor residential and rural well-water (Williston, Watford City, Dickinson) on Fox Hills-Hell Creek aquifer and well-water supply. Rural North Dakota well-water statewide variable depending on aquifer source.",
    "hardnessLow": 80,
    "hardnessHigh": 450,
    "dominantTier": "moderately hard (Fargo, West Fargo); hard (Bismarck, Minot); 1 of 5 covered cities publishes no figure",
    "topCitiesCount": 8,
    "contaminantsCount": 7
  },
  {
    "slug": "south-dakota",
    "name": "South Dakota",
    "abbreviation": "SD",
    "region": "Midwest",
    "assignedAuthor": "jan-davenport",
    "summary": "South Dakota runs as four working zones. Sioux Falls and the eastern corridor on Sioux Falls Water Big Sioux aquifer and surface-supplemented supply. Rapid City and the Black Hills corridor on Rapid City Water mountain-source and Madison-aquifer-supplemented supply. The central South Dakota Pierre and Missouri River corridor on Missouri River-source and aquifer-supplemented supply. Western South Dakota rural well-water through the Sandhills-adjacent and Pine Ridge-adjacent corridors on local aquifer and well-water systems. Reservation-adjacent residential through Pine Ridge, Rosebud, Cheyenne River, Standing Rock, Crow Creek, and Lower Brule operates on its own residential category.",
    "hardnessLow": 100,
    "hardnessHigh": 400,
    "dominantTier": "very hard (Sioux Falls); 4 of 5 covered cities publish no figure",
    "topCitiesCount": 9,
    "contaminantsCount": 7
  },
  {
    "slug": "vermont",
    "name": "Vermont",
    "abbreviation": "VT",
    "region": "New England",
    "assignedAuthor": "abby-giordano",
    "summary": "Vermont runs as three working zones. Burlington and the Champlain Valley corridor on Champlain Water District Lake Champlain-source surface supply. Central Vermont and the Connecticut River Valley corridor (Montpelier, Barre, White River Junction, Brattleboro) on mixed municipal supplies. Northern Vermont and the Northeast Kingdom rural corridor on local aquifer and well-water systems. Rural Vermont well-water statewide variable depending on aquifer source and geology.",
    "hardnessLow": 80,
    "hardnessHigh": 340,
    "dominantTier": "soft (Colchester, Essex, South Burlington); moderately hard (Burlington); 1 of 5 covered cities publishes no figure",
    "topCitiesCount": 9,
    "contaminantsCount": 7
  },
  {
    "slug": "new-hampshire",
    "name": "New Hampshire",
    "abbreviation": "NH",
    "region": "New England",
    "assignedAuthor": "abby-giordano",
    "summary": "New Hampshire runs as five working zones. Manchester and the Merrimack Valley corridor on Manchester Water Works Lake Massabesic-source surface supply. Nashua and the southern NH border-corridor on Pennichuck Corporation surface-and-aquifer-supplemented supply. Concord and the central NH corridor on Concord General Services Penacook Lake-source surface supply. Portsmouth and the Seacoast corridor on Aquarion Water Bellamy Reservoir-source supply with substantial coastal salt-aerosol overlay. The White Mountains and Lakes Region tourism-corridor on mixed municipal and aquifer supplies. Rural New Hampshire well-water through the Granite State backcountry on local aquifer and well-water systems.",
    "hardnessLow": 80,
    "hardnessHigh": 340,
    "dominantTier": "soft (Portsmouth, Nashua, Concord); moderately hard to hard by the utility's own description (Dover); 1 of 5 covered cities publishes no figure",
    "topCitiesCount": 9,
    "contaminantsCount": 8
  },
  {
    "slug": "maine",
    "name": "Maine",
    "abbreviation": "ME",
    "region": "New England",
    "assignedAuthor": "abby-giordano",
    "summary": "Maine runs as five working zones. Portland and the southern coastal corridor on Portland Water District Sebago Lake-source surface supply (among the cleaner municipal supplies in the country). Bangor and central Maine on Bangor Water District surface-and-aquifer-supplemented supply. The Down East coastal corridor on mixed municipal supplies with substantial coastal salt-aerosol overlay. Northern Maine and the Aroostook County corridor on mixed municipal and aquifer supplies. The lakes-and-mountains corridor on mixed mountain-source and aquifer-supplemented supplies. Rural Maine well-water through the inland backcountry on local aquifer and well-water systems.",
    "hardnessLow": 80,
    "hardnessHigh": 340,
    "dominantTier": "soft (Auburn, Lewiston, Portland, South Portland, Bangor)",
    "topCitiesCount": 9,
    "contaminantsCount": 8
  },
  {
    "slug": "idaho",
    "name": "Idaho",
    "abbreviation": "ID",
    "region": "Mountain West",
    "assignedAuthor": "easton-giordano",
    "summary": "Idaho runs as five working zones. Boise and the Treasure Valley on Boise Water's Snake River Plain aquifer supply, with a substantial concentration of post-2010 boom-era coated glass. Idaho Falls and eastern Idaho on Snake River-and-aquifer-supplemented supply; Idaho Falls reports very hard water. Coeur d'Alene and the northern Idaho panhandle on Spokane Valley-Rathdrum Prairie aquifer-and-surface supply. The Sun Valley and Ketchum ski corridor on mountain-source and aquifer-supplemented supplies with a high-elevation UV overlay. The Sandpoint and Lake Pend Oreille tourism corridor on lake-source supply. Rural Idaho well-water varies with the local aquifer.",
    "hardnessLow": 100,
    "hardnessHigh": 400,
    "dominantTier": "moderately hard (Meridian, Nampa); very hard (Idaho Falls); 2 of 5 covered cities publish no figure",
    "topCitiesCount": 10,
    "contaminantsCount": 8
  },
  {
    "slug": "arkansas",
    "name": "Arkansas",
    "abbreviation": "AR",
    "region": "South",
    "assignedAuthor": "cal-hatcher",
    "summary": "Arkansas runs as four working zones. Little Rock and central Arkansas on Central Arkansas Water's Lake Maumelle and Lake Winona surface supply. Northwest Arkansas (Fayetteville, Bentonville, Rogers, Springdale) on Beaver Water District's Beaver Lake surface supply, which Fayetteville and Springdale both report as moderately hard. The Arkansas River Valley corridor (Fort Smith, Russellville) on Arkansas River-and-aquifer-supplemented supply. The Delta and eastern Arkansas (Jonesboro, West Memphis, Pine Bluff) on Mississippi Embayment alluvial-aquifer supply. The Ozark and Ouachita rural well-water on local aquifer and well-water systems.",
    "hardnessLow": 80,
    "hardnessHigh": 340,
    "dominantTier": "moderately hard (Fayetteville, Springdale); 3 of 5 covered cities publish no figure",
    "topCitiesCount": 10,
    "contaminantsCount": 7
  },
  {
    "slug": "montana",
    "name": "Montana",
    "abbreviation": "MT",
    "region": "Mountain West",
    "assignedAuthor": "easton-giordano",
    "summary": "Montana runs as four working zones. Billings and the Yellowstone Valley corridor on Billings Water Yellowstone River-and-aquifer-supplemented supply. Missoula and the western Montana corridor on Missoula Water (formerly Mountain Water Company) aquifer supply. The Bozeman, Helena, Great Falls, and central Montana corridor on mixed municipal supplies; Great Falls reports hard water. The northwest Montana lakes corridor (Kalispell, Whitefish, Polson) on Flathead aquifer-and-lake-source supply. The Bakken-adjacent eastern Montana corridor (Glendive, Sidney, Wolf Point, Miles City) on aquifer and well-water systems. Rural Montana well-water varies from well to well.",
    "hardnessLow": 130,
    "hardnessHigh": 400,
    "dominantTier": "hard (Great Falls, Billings, Bozeman); 2 of 5 covered cities publish no figure",
    "topCitiesCount": 9,
    "contaminantsCount": 8
  },
  {
    "slug": "west-virginia",
    "name": "West Virginia",
    "abbreviation": "WV",
    "region": "Appalachian",
    "assignedAuthor": "wade-marler",
    "summary": "West Virginia runs as seven working zones. Charleston and the Kanawha Valley corridor on West Virginia American Water Elk River and Kanawha River-supplemented supply. Huntington and the Tri-State Ohio River corridor on West Virginia American Water Ohio River-source supply. Morgantown and the north-central corridor on Morgantown Utility Board Cobun Creek and Monongahela River-source supply. Wheeling and the northern panhandle on Wheeling Water Ohio River-source supply. The Eastern Panhandle (Martinsburg, Charles Town, Harpers Ferry, Shepherdstown) on mixed municipal supplies. The southern coal corridor (Beckley, Bluefield, Princeton, Welch, Logan) on mixed municipal supplies. Rural Appalachian well-water varies from well to well.",
    "hardnessLow": 140,
    "hardnessHigh": 380,
    "dominantTier": "soft (Charleston); moderately hard (Huntington, Morgantown); 2 of 5 covered cities publish no figure",
    "topCitiesCount": 9,
    "contaminantsCount": 8
  },
  {
    "slug": "wyoming",
    "name": "Wyoming",
    "abbreviation": "WY",
    "region": "Mountain West",
    "assignedAuthor": "easton-giordano",
    "summary": "Wyoming runs as six working zones. Cheyenne and the southeastern Wyoming corridor on Cheyenne Board of Public Utilities Crow Creek and aquifer-supplemented supply. Casper and the central Wyoming corridor on Casper Water and Casper Mountain-source supply. Laramie and the high-elevation southeastern Wyoming corridor on Casper Aquifer-source supply. Jackson Hole and the Teton corridor on Jackson Hole municipal supplies. The Yellowstone-adjacent Cody corridor and northwest Wyoming on Cody Water and surrounding municipal supplies. The Bakken-adjacent Powder River Basin corridor (Gillette, Sheridan, Buffalo, Wright, Newcastle) on Gillette-Madison Water and surrounding municipal supplies — among the hardest municipal water in the Mountain West. Rural ranching well-water varies from well to well.",
    "hardnessLow": 140,
    "hardnessHigh": 400,
    "dominantTier": "soft (Cheyenne); extremely hard (Gillette); 3 of 5 covered cities publish no figure",
    "topCitiesCount": 9,
    "contaminantsCount": 8
  },
  {
    "slug": "alaska",
    "name": "Alaska",
    "abbreviation": "AK",
    "region": "Pacific Northwest",
    "assignedAuthor": "easton-giordano",
    "summary": "Alaska runs as four working zones. Anchorage and the Cook Inlet corridor on Anchorage Water and Wastewater Utility's Eklutna Lake and Ship Creek surface supply, which the utility's own figures put in the moderately hard band. The Mat-Su Valley and Kenai Peninsula corridor on mixed municipal and well-water supplies. Fairbanks and the interior corridor on Golden Heart Utilities' Chena River and aquifer supply, with a substantial iron-and-manganese fraction. Juneau and the Southeast Panhandle on precipitation-fed municipal supplies. Rural bush Alaska well-water and surface-water supply varies from village to village.",
    "hardnessLow": 40,
    "hardnessHigh": 400,
    "dominantTier": "moderately hard (Anchorage); hard (Wasilla-Palmer, Fairbanks); 2 of 5 covered cities publish no figure",
    "topCitiesCount": 9,
    "contaminantsCount": 8
  },
  {
    "slug": "hawaii",
    "name": "Hawaii",
    "abbreviation": "HI",
    "region": "Pacific Northwest",
    "assignedAuthor": "easton-giordano",
    "summary": "Hawaii runs as four working zones across the four major inhabited islands. Oahu and the Honolulu metro on Honolulu Board of Water Supply aquifer supply, leeward from the Pearl Harbor aquifer and windward through Kailua-Kaneohe. Maui on Maui Department of Water Supply mixed-aquifer supply, with Upcountry and the East Maui Hana coast on different sources. The Big Island on County of Hawaii Department of Water Supply mixed supply: surface and rain-fed on the Hilo side, aquifer-fed on the Kona side. Kauai on Kauai Department of Water aquifer-and-surface supply. None of these utilities publishes a hardness figure for the cities we cover.",
    "hardnessLow": 60,
    "hardnessHigh": 220,
    "dominantTier": "not published",
    "topCitiesCount": 12,
    "contaminantsCount": 8
  }
];

// Convenience accessors that operate on the lightweight index only.
// Callers that need full state data should dynamically import the
// per-state module via getStateModuleLoader(slug).
export function getStateSummaryBySlug(slug) {
  return STATES_INDEX.find((s) => s.slug === slug) || null;
}

export function getStateSummariesByRegion(region) {
  return STATES_INDEX.filter((s) => s.region === region);
}

export function getStateSummariesByAuthor(authorSlug) {
  return STATES_INDEX.filter((s) => s.assignedAuthor === authorSlug);
}

// Vite lazy glob — every per-state module under ./states/<slug>.js,
// keyed by its relative path. Each value is a function that returns a
// promise resolving to the module. Rollup emits one chunk per state.
//
// We don't expose `stateModules` directly. Callers should use
// `loadStateBySlug(slug)`, which abstracts the path-key lookup and
// unwraps the default export. The glob pattern excludes `index.js`
// itself (the only sibling that doesn't represent a state).
const stateModules = import.meta.glob('./*.js');

// Load the full state record by slug. Returns a promise resolving to
// the full state object (the per-state module's default export) or
// null if no module exists for the slug. Only the matching state's
// chunk is fetched.
export function loadStateBySlug(slug) {
  if (!slug) return Promise.resolve(null);
  // Path keys are ./<slug>.js relative to this index file.
  // Reject the index.js path-key (this file) explicitly — a slug of
  // 'index' is not a valid state and would otherwise return STATES_INDEX
  // wrapped as a default-less module.
  if (slug === 'index') return Promise.resolve(null);
  const key = `./${slug}.js`;
  const loader = stateModules[key];
  if (!loader) return Promise.resolve(null);
  return loader().then((mod) => mod.default || null);
}
